import {Container, Graphics, InteractionEvent, Point, Sprite} from 'pixi.js';
import {UpdateManager} from '@gongxigames/framework/core/services/UpdateManager';

export type ScrollContainerOptions = {
    boundsWidth: number;
    boundsHeight: number;
    scrollSize?: number;
    centeringContent?: boolean;
    decelerationFactor?: number;
};

export class ScrollContainer extends Container {
    protected _boundsWidth = 0;
    protected _boundsHeight = 0;
    protected _scrollSize = 0;
    protected _centeringContent = false;
    protected _decelerationFactor = 0.96;
    protected _isDown = false;
    protected _oldPoint: Point | undefined;
    protected _scrollbar: Graphics;
    protected _maskContent: Graphics;
    protected _content: Container;
    protected _scrollContainerBounds: Sprite;
    protected _savedPoints: number[] = [];
    protected _velocity = 0;

    protected redrawCallback: (dt: number) => void = this.redraw.bind(this);
    protected updateVelocityCallback: (dt: number) => void = this.applyVelocity.bind(this);
    protected onMouseWheelCallback: (event: WheelEvent) => void = this.onMouseWheel.bind(this);

    constructor(
        options: ScrollContainerOptions,
        private readonly updateManager: UpdateManager,
        private readonly canvas: HTMLCanvasElement
    ) {
        super();

        this._boundsWidth = options.boundsWidth;
        this._boundsHeight = options.boundsHeight;
        this._scrollSize = options.scrollSize || 20;
        this._centeringContent = options.centeringContent || false;
        // stochastic value according personal experiments, can be configurable
        this._decelerationFactor = options.decelerationFactor || 0.96;

        this._maskContent = this.addChild(new Graphics());
        this._scrollContainerBounds = this.addChild(new Sprite());
        this._content = this.addChild(new Container());
        this._scrollbar = this.addChild(new Graphics());

        this._scrollbar.interactive = true;
        this._scrollbar.on('pointerdown', this.scrollbarDown, this);

        this.interactive = true;
        this.on('pointerdown', this.scrollbarDown, this);
        this.on('pointermove', this.scrollbarMove, this);
        this.on('pointerup', this.scrollbarUp, this);
        this.on('pointercancel', this.scrollbarUp, this);
        this.on('pointerupoutside', this.scrollbarUp, this);
        this.on('added', this.onAdded, this);
        this.on('removed', this.onRemoved, this);

        this._content.on('childAdded', () => this.update());
        this._content.on('removedFrom', () => this.update());
        this._content.mask = this._maskContent;

        this.addChild = this._content.addChild.bind(this._content);
        this.addChildAt = this._content.addChildAt.bind(this._content);
        this.swapChildren = this._content.swapChildren.bind(this._content);
        this.getChildIndex = this._content.getChildIndex.bind(this._content);
        this.setChildIndex = this._content.setChildIndex.bind(this._content);
        this.getChildAt = this._content.getChildAt.bind(this._content);
        this.removeChild = this._content.removeChild.bind(this._content);
        this.removeChildAt = this._content.removeChildAt.bind(this._content);
        this.removeChildren = this._content.removeChildren.bind(this._content);
    }

    protected onAdded(): void {
        this.update();
        this.canvas.addEventListener('wheel', this.onMouseWheelCallback, { passive: false });
        this.updateManager.addUpdateFunction(this.redrawCallback);
        this.updateManager.addUpdateFunction(this.updateVelocityCallback);
    }

    protected onRemoved(): void {
        this.canvas.removeEventListener('wheel', this.onMouseWheelCallback);
        this.updateManager.removeUpdateFunction(this.redrawCallback);
        this.updateManager.removeUpdateFunction(this.updateVelocityCallback);
    }

    protected onMouseWheel(event: WheelEvent): void {
        event.preventDefault();
        this._content.y -= event.deltaY;

        this.handleBounds();
    };

    protected redraw(): void {
        if (this._centeringContent) {
            this._content.x = (this._boundsWidth - this._content.width) / 2;
        }
        this.drawScrollBar();
    }

    protected update(): void {
        if (this._destroyed) {
            return;
        }

        this._content.y = 0;
        this.udateBounds();
        this.drawScrollBar();
        this.drawMask();
    }

    protected udateBounds(): void {
        this._scrollContainerBounds.width = this._boundsWidth;
        this._scrollContainerBounds.height = this._boundsHeight;
    }

    protected drawMask(): void {
        if (this._maskContent.parent) {
            this._maskContent
                .clear()
                .beginFill(0)
                .drawRect(0, 0, this._boundsWidth, this._boundsHeight)
                .endFill();
        }
    }

    protected scrollbarDown(e: InteractionEvent): void {
        if (!this.hasScroll) {
            return;
        }

        this._isDown = true;
        this._oldPoint = e.data.global.clone();
    }

    protected scrollbarUp(e: InteractionEvent): void {
        if (!this.hasScroll) {
            return;
        }

        this._isDown = false;
        this._oldPoint = undefined;

        if (this._savedPoints.length > 1) {
            this.calculateVelocity();
        }
    }

    protected calculateVelocity(): void {
        let accumulatedDifference = 0;
        let numberOfPoints = 0;

        // get 3 points for interpolation (stochastic value)
        for (let i = 1; i < 4; i++) {
            const l = this._savedPoints.length;
            const a = this._savedPoints[l - i];
            const b = this._savedPoints[l - i - 1];

            if (a && b) {
                numberOfPoints++;
                accumulatedDifference += a - b;
            }
        }
        this._velocity = accumulatedDifference / numberOfPoints;
        this._savedPoints.length = 0;
    }

    protected handleBounds(): void {
        // It sets the desired position when leaving abroad
        if (this.outOfBottom) {
            this._content.y = this._boundsHeight - this._content.height;
        } else if (this.outOfTop) {
            this._content.y = 0;
        }
    }

    protected applyVelocity(): void {
        if (Math.abs(this._velocity) > 1) {
            this._content.y += this._velocity;
            this._velocity *= this._decelerationFactor;
            this.handleBounds();
        }
    }

    protected scrollbarMove(e: InteractionEvent): void {
        if (this.hasScroll && this._isDown && this._oldPoint) {
            let dy = this._oldPoint.y - e.data.global.y;
            const topBounds = this.height - this._content.height;

            // slowdown of movement when going out of bounds
            if (this.outOfTop) {
                dy = dy * (1 - (this._content.y / (this.height * 0.3)));
            } else if (this._content.y < topBounds) {
                const delta = Math.abs(this._content.y) + topBounds;

                dy = dy * (1 - (delta / (this._boundsHeight * 0.3)));
            }

            this._content.y -= dy;
            this._oldPoint = e.data.global.clone();
            this._savedPoints.push(this._oldPoint?.y);
        }
    }

    protected get hasScroll(): boolean {
        return this._content.height > this._boundsHeight;
    }

    protected drawScrollBar(): void {
        const scrollbarHeight = (this._boundsHeight / this._content.height) * this._boundsHeight;
        let offset = Math.abs(this._content.y < 0 ? this._content.y : 0);

        if (this.outOfBottom) {
            offset = Math.abs(this._boundsHeight - this._content.height);
        }

        const scrollbarY = offset / (this._content.height - this._boundsHeight) * (this._boundsHeight - scrollbarHeight);

        if (this._scrollbar.parent) {
            this._scrollbar.clear();
            this._scrollbar
                .beginFill(0xdddddd, 1)
                .drawRect(this._boundsWidth - this._scrollSize, 0, this._scrollSize, this._boundsHeight)
                .endFill();

            this._scrollbar
                .beginFill(0x888888, 1)
                .drawRect(this._boundsWidth - this._scrollSize, scrollbarY, this._scrollSize, scrollbarHeight)
                .endFill();
        }

        this._scrollContainerBounds.visible = this._scrollbar.visible = this.hasScroll;
    }

    protected get outOfBottom(): boolean {
        return this._content.y < this._boundsHeight - this._content.height;
    }

    protected get outOfTop(): boolean {
        return this._content.y > 0;
    }

    public get boundsWidth(): number {
        return this._boundsWidth;
    }

    public set boundsWidth(value: number) {
        this._boundsWidth = value;
        this.update();
    }

    public get boundsHeight(): number {
        return this._boundsHeight;
    }

    public set boundsHeight(value: number) {
        this._boundsHeight = value;
        this.update();
    }

    public get scrollSize(): number {
        return this._scrollSize;
    }

    public set scrollSize(value: number) {
        this._scrollSize = value;
        this.update();
    }
}
